import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PageContent } from '../components/layout/PageContent';
import { CabecalhoPagina } from '../components/layout/CabecalhoPagina';
import { BarraAcoes } from '../components/common/Formulario';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Loader2, Edit, Trash2, MapPin, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { championshipApi } from '../lib/api';
import { EstadoVazio } from '../components/common/Estados';

export default function EventStructurePage() {
  const [championships, setChampionships] = useState([]);
  const params = useParams();
  const contextId = params.id || '';
  const [selectedChamp, setSelectedChamp] = useState(contextId);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    area_number: 1,
    area_name: '',
    judge_name: '',
    staff_names: ['']
  });

  useEffect(() => {
    const fetchChampionships = async () => {
      try {
        const { data } = await championshipApi.list();
        setChampionships(data);
        // Preserve context from URL — only default to first championship if no context
        if (!contextId && data.length > 0) {
          setSelectedChamp(data[0]._id);
        }
      } catch (e) { toast.error('Erro ao carregar campeonatos'); }
      finally { setLoading(false); }
    };
    fetchChampionships();
  }, []);

  useEffect(() => {
    if (selectedChamp) fetchAreas();
  }, [selectedChamp]);

  const fetchAreas = async () => {
    try {
      const { data } = await api.get(`/event-structure?championship_id=${selectedChamp}`);
      setAreas(data);
    } catch (e) { toast.error('Erro ao carregar estrutura'); }
  };

  const resetForm = () => {
    const nextNum = areas.length > 0 ? Math.max(...areas.map(a => a.area_number)) + 1 : 1;
    setFormData({ area_number: nextNum, area_name: '', judge_name: '', staff_names: [''] });
    setEditingArea(null);
  };

  const handleEdit = (area) => {
    setFormData({
      area_number: area.area_number,
      area_name: area.area_name || '',
      judge_name: area.judge_name || '',
      staff_names: area.staff_names?.length > 0 ? area.staff_names : ['']
    });
    setEditingArea(area);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este tatame?')) return;
    try {
      await api.delete(`/event-structure/${id}`);
      toast.success('Tatame excluido');
      fetchAreas();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedChamp) return;
    setSaving(true);
    try {
      const payload = {
        championship_id: selectedChamp,
        area_number: parseInt(formData.area_number),
        area_name: formData.area_name || `Tatame ${formData.area_number}`,
        judge_name: formData.judge_name,
        staff_names: formData.staff_names.filter(s => s.trim())
      };
      if (editingArea) {
        await api.put(`/event-structure/${editingArea._id}`, payload);
        toast.success('Tatame atualizado');
      } else {
        await api.post('/event-structure', payload);
        toast.success('Tatame criado');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchAreas();
    } catch (e) { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const addStaff = () => setFormData({ ...formData, staff_names: [...formData.staff_names, ''] });
  const removeStaff = (i) => setFormData({ ...formData, staff_names: formData.staff_names.filter((_, idx) => idx !== i) });
  const updateStaff = (i, val) => {
    const updated = [...formData.staff_names];
    updated[i] = val;
    setFormData({ ...formData, staff_names: updated });
  };

  return (
    <PageContent>
      <div className="space-y-6" data-testid="event-structure-page">
        {/* Header — título/contexto à esquerda, seleção à direita */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <CabecalhoPagina
            className="min-w-0 flex-1"
            contexto="Configuração"
            titulo="Estrutura do Evento"
            descricao="Gerencie tatames, juizes e staff por campeonato"
            icone={MapPin}
          />
          <div className="flex gap-3 items-center">
            <Select value={selectedChamp || "none"} onValueChange={(v) => setSelectedChamp(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="event-championship-select" className="bg-[#1A1A1A] border-[#27272A] text-white w-[250px]">
                <SelectValue placeholder="Selecione o campeonato..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                {championships.map(c => (
                  <SelectItem key={c._id} value={c._id} className="text-white hover:bg-[#27272A]">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button disabled={!selectedChamp} data-testid="new-area-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold" onClick={resetForm}>
                  <Plus size={16} className="mr-2" /> Novo Tatame
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#141414] border-[#27272A] max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white font-['Chivo']">{editingArea ? 'Editar Tatame' : 'Novo Tatame'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Numero</Label>
                      <Input type="number" value={formData.area_number} onChange={(e) => setFormData({ ...formData, area_number: e.target.value })} required data-testid="area-number" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA]">Nome</Label>
                      <Input value={formData.area_name} onChange={(e) => setFormData({ ...formData, area_name: e.target.value })} placeholder="Tatame 1" data-testid="area-name" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Juiz / Arbitro</Label>
                    <Input value={formData.judge_name} onChange={(e) => setFormData({ ...formData, judge_name: e.target.value })} placeholder="Nome do juiz" data-testid="area-judge" className="bg-[#1A1A1A] border-[#27272A] text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Staff / Mesarios</Label>
                    {formData.staff_names.map((s, i) => (
                      <div key={i} className="flex gap-2">
                        <Input value={s} onChange={(e) => updateStaff(i, e.target.value)} placeholder={`Staff ${i + 1}`} className="bg-[#1A1A1A] border-[#27272A] text-white" />
                        {formData.staff_names.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeStaff(i)} className="text-red-400 hover:text-red-300 px-2"><Trash2 size={16} /></Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addStaff} className="border-[#27272A] text-[#A1A1AA] w-full">
                      <Plus size={16} className="mr-1" /> Adicionar Staff
                    </Button>
                  </div>
                  <BarraAcoes contexto="dialogo" className="pt-2">
                    <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="border-[#27272A] text-[#A1A1AA]">Cancelar</Button>
                    <Button type="submit" disabled={saving} data-testid="save-area" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingArea ? 'Salvar' : 'Criar')}
                    </Button>
                  </BarraAcoes>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : !selectedChamp ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={MapPin} titulo="Selecione um campeonato" descricao="Escolha o campeonato para configurar a estrutura do evento" />
            </CardContent>
          </Card>
        ) : areas.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={MapPin} titulo="Nenhum tatame configurado" descricao="Adicione tatames, juizes e staff para este campeonato" />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {areas.map((area) => (
              <Card key={area._id} className="bg-[#141414] border-[#27272A] hover:border-[#D4AF37]/30 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-white font-['Chivo'] flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] font-bold text-sm">
                        {area.area_number}
                      </div>
                      {area.area_name || `Tatame ${area.area_number}`}
                    </CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(area)} className="text-[#A1A1AA] hover:text-white"><Edit size={16} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(area._id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <User size={16} className="text-[#D4AF37]" />
                    <span className="text-[#A1A1AA]">Juiz:</span>
                    <span className="text-white">{area.judge_name || 'Nao definido'}</span>
                  </div>
                  {area.staff_names?.length > 0 && area.staff_names.some(s => s) && (
                    <div className="flex items-start gap-2 text-sm">
                      <Users size={16} className="text-[#D4AF37] mt-0.5" />
                      <div>
                        <span className="text-[#A1A1AA]">Staff:</span>
                        {area.staff_names.filter(s => s).map((s, i) => (
                          <span key={i} className="ml-1 text-white">{s}{i < area.staff_names.filter(n => n).length - 1 ? ',' : ''}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PageContent>
  );
}
